// ================================================================
// 管理員儀表板頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：系統統計與快速概覽
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People,
  QueryStats,
  AccountBalanceWallet,
  TrendingUp,
  Refresh,
  Settings,
  CurrencyExchange,
} from '@mui/icons-material';
import { getSystemStats, SystemStats } from '../../api/admin';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';

// ================================================================
// 統計卡片組件
// ================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card
    sx={{
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      },
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiSvgIcon-root': {
              fontSize: 32,
              color: color,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ================================================================
// 主要頁面組件
// ================================================================

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAdmin();

  const [stats, setStats] = useState<SystemStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 載入統計資料
  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getSystemStats();
      // 後端回傳 response.stats，不是 response.data
      const rawStats = (response as any).stats || response.data;
      if (response.success && rawStats) {
        // 轉換後端資料格式為前端期待的格式
        const totalQueries = rawStats.category_stats?.reduce(
          (sum: number, cat: any) => sum + (cat.count || 0), 0
        ) || 0;
        const transformedStats: SystemStats = {
          users: {
            total: rawStats.users?.total || 0,
            active: rawStats.users?.active || 0,
            suspended: rawStats.users?.suspended || 0,
            new_this_week: 0, // 後端未提供
            new_this_month: rawStats.month?.new_users || 0,
          },
          queries: {
            total: totalQueries,
            today: rawStats.today?.queries || 0,
            this_week: 0, // 後端未提供
            this_month: rawStats.month?.queries || 0,
          },
          transactions: {
            count: totalQueries, // 使用查詢總數作為交易筆數
            total_amount: rawStats.users?.total_revenue || 0,
          },
          today: rawStats.today || { new_users: 0, queries: 0, revenue: 0 },
          month: rawStats.month || { new_users: 0, queries: 0, revenue: 0 },
          weekly_trend: rawStats.weekly_trend || [],
          category_stats: rawStats.category_stats || [],
        };
        setStats(transformedStats);
      } else {
        setError(response.message || '無法載入統計資料');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // 格式化數字
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('zh-TW').format(num);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              儀表板
            </Typography>
            <Typography variant="body2" color="text.secondary">
              歡迎回來，{admin?.full_name || admin?.username}
            </Typography>
          </Box>
          <Tooltip title="重新整理">
            <IconButton onClick={loadStats} disabled={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 統計卡片 */}
        {stats && (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="總使用者數"
                  value={formatNumber(stats.users.total)}
                  icon={<People />}
                  color="#1976d2"
                  subtitle={`活躍: ${stats.users.active}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="總查詢次數"
                  value={formatNumber(stats.queries.total)}
                  icon={<QueryStats />}
                  color="#2e7d32"
                  subtitle={`今日: ${stats.queries.today}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="總交易金額"
                  value={`NT$ ${formatNumber(stats.transactions.total_amount)}`}
                  icon={<AccountBalanceWallet />}
                  color="#ed6c02"
                  subtitle={`筆數: ${stats.transactions.count}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="本月新增用戶"
                  value={formatNumber(stats.users.new_this_month)}
                  icon={<TrendingUp />}
                  color="#9c27b0"
                  subtitle={`本週: ${stats.users.new_this_week}`}
                />
              </Grid>
            </Grid>

            {/* 快速操作 */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      快速操作
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Card
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => navigate('/admin/users')}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <People color="primary" />
                            <Typography>使用者管理</Typography>
                          </Box>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => navigate('/admin/currencies')}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <CurrencyExchange color="primary" />
                            <Typography>匯率管理</Typography>
                          </Box>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => navigate('/admin/settings')}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Settings color="primary" />
                            <Typography>系統設定</Typography>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      管理員資訊
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="text.secondary">帳號</Typography>
                        <Typography fontWeight="bold">{admin?.username}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="text.secondary">角色</Typography>
                        <Chip
                          label={
                            admin?.role === 'super_admin'
                              ? '超級管理員'
                              : admin?.role === 'admin'
                              ? '管理員'
                              : '客服人員'
                          }
                          color={admin?.role === 'super_admin' ? 'error' : 'primary'}
                          size="small"
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="text.secondary">電子郵件</Typography>
                        <Typography>{admin?.email || '-'}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography color="text.secondary">姓名</Typography>
                        <Typography>{admin?.full_name || '-'}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
          </>
        )}
      </Box>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
